import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { videoCallAPI } from '../services/api';
import './IncomingCallModal.css';

const IncomingCallModal = ({ call, onAnswer, onDecline, onTimeout }) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(30);
  const [answering, setAnswering] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Countdown timer
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onTimeout]);

  const handleAnswer = async () => {
    setAnswering(true);
    setError(null);

    // Navigate immediately for better UX
    navigate(`/call/${call.id}`);
    onAnswer(call);

    // Answer the call in the background
    try {
      await videoCallAPI.answerCall(call.id);
    } catch (err) {
      // Don't show error - the call screen will handle connection issues
    }
  };

  const handleDecline = async () => {
    setDeclining(true);
    setError(null);
    try {
      await videoCallAPI.declineCall(call.id);
      onDecline();
    } catch (err) {
      console.error('Failed to decline call:', err);
      setError('Failed to decline. Please try again.');
      setDeclining(false);
    }
  };

  return (
    <div className="incoming-call-overlay">
      <div className="incoming-call-modal">
        <div className="incoming-call-header">
          <div className="caller-avatar">
            {call.callerPhotoUrl ? (
              <img src={call.callerPhotoUrl} alt={call.callerName} />
            ) : (
              <div className="avatar-placeholder">
                {call.callerName?.charAt(0) || '?'}
              </div>
            )}
          </div>
          <h2>{call.callerName || 'Unknown'}</h2>
          <p className="call-type">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: '8px' }}>
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            Incoming Video Call
          </p>
        </div>

        <div className="incoming-call-body">
          {error && (
            <div className="call-error-banner">
              <p>{error}</p>
            </div>
          )}
          
          <div className="call-timer">
            <div className="timer-circle">
              <svg className="timer-svg" viewBox="0 0 100 100">
                <circle
                  className="timer-bg"
                  cx="50"
                  cy="50"
                  r="45"
                />
                <circle
                  className="timer-progress"
                  cx="50"
                  cy="50"
                  r="45"
                  style={{
                    strokeDasharray: `${(timeLeft / 30) * 283} 283`,
                    transition: 'stroke-dasharray 1s linear'
                  }}
                />
              </svg>
              <div className="timer-text">{timeLeft}s</div>
            </div>
            <p className="timer-label">Answer before timeout</p>
          </div>

          {/* Credit/rate info removed - video calls are free */}
        </div>

        <div className="incoming-call-actions">
          <button
            className="btn-decline"
            onClick={handleDecline}
            disabled={declining || answering}
          >
            {declining ? 'Declining...' : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
                Decline
              </>
            )}
          </button>
          <button
            className="btn-answer"
            onClick={handleAnswer}
            disabled={answering || declining}
          >
            {answering ? 'Answering...' : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Answer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallModal;
